<template>
  <div class="tog">
    <!-- 🔴 ONE root element on purpose, and this comment lives INSIDE it. The parent renders this with
         `:data-testid="tpsv2-field-<key>"` and relies on attribute fall-through, and `jumpToField` then does
         `host.querySelector('input, textarea').focus()` — so the id / find boxes below have to be inside the
         element that carries the testid, and nothing may sit beside that element at the template root. -->
    <!-- ── สถานะ: ชุดเริ่มต้น / ของตัวเอง / ปิดทุกกฎ / อ่านไม่ออก ─────────────────────────────
         Three states that a single "is there text in the column" test cannot tell apart, so they get three
         different sentences and two different buttons. `''` = ยังไม่ override · `[]` = override ที่ว่าง
         (ตัวตรวจยังเดิน แต่ไม่มีกฎให้รัน) · list = ของตัวเอง. -->
    <v-alert
      v-bind="statusProps"
      variant="tonal"
      density="compact"
      class="tog-status"
      data-testid="tog-status"
    >
      <div class="d-flex align-center flex-wrap ga-2">
        <span class="flex-grow-1">{{ statusText }}</span>

        <v-btn
          v-if="mode === 'default'"
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-playlist-edit"
          data-testid="tog-adopt"
          @click="adoptDefaults"
        >
          แก้เป็นของตัวเอง
        </v-btn>
        <v-btn
          v-else
          size="small"
          variant="text"
          color="primary"
          prepend-icon="mdi-backup-restore"
          data-testid="tog-revert"
          @click="revertToDefaults"
        >
          กลับไปใช้ชุดเริ่มต้น
        </v-btn>
      </div>
    </v-alert>

    <!-- ปัญหาระดับ "ทั้งชุด" (index -1) — ไม่มีแถวให้แปะ จึงขึ้นเหนือตาราง -->
    <v-alert
      v-for="(msg, i) in setErrors"
      :key="`set-${i}`"
      type="error"
      variant="tonal"
      density="compact"
      class="tog-alert"
      :data-testid="`tog-set-error-${i}`"
    >
      {{ msg }}
    </v-alert>

    <!-- error ที่พ่อส่งมา (fieldErrors ของ dialog) — โหมด JSON ขั้นสูงยังพิมพ์ให้พังได้ -->
    <v-alert
      v-for="(msg, i) in errorMessages"
      :key="`parent-${i}`"
      type="error"
      variant="tonal"
      density="compact"
      class="tog-alert"
      :data-testid="`tog-field-error-${i}`"
    >
      {{ msg }}
    </v-alert>

    <!-- ══════════════ ตาราง ══════════════ -->
    <div v-if="!showJson" class="tog-rows" data-testid="tog-rows">
      <div
        v-for="(row, idx) in shownRows"
        :key="idx"
        class="tog-row"
        :class="{ 'is-off': row.enabled === false }"
        :data-testid="`tog-row-${idx}`"
      >
        <div class="tog-row-head">
          <v-checkbox-btn
            :model-value="row.enabled !== false"
            :readonly="readOnly"
            density="compact"
            hide-details
            color="primary"
            aria-label="เปิด/ปิดกฎข้อนี้"
            :data-testid="`tog-enabled-${idx}`"
            @update:model-value="(v) => setField(idx, 'enabled', v === true)"
          />
          <span class="tog-index">{{ idx + 1 }}</span>

          <v-text-field
            :model-value="row.id"
            :readonly="readOnly"
            label="ชื่อกฎ (id)"
            placeholder="เช่น brk.tag"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="tog-id"
            :error-messages="errorsFor(idx, 'id')"
            :data-testid="`tog-id-${idx}`"
            @update:model-value="(v) => setField(idx, 'id', v)"
          />

          <v-select
            :model-value="row.kind === 'op' ? 'op' : 'regex'"
            :items="KIND_ITEMS"
            item-title="title"
            item-value="value"
            :readonly="readOnly"
            label="ชนิด"
            variant="outlined"
            density="compact"
            hide-details
            class="tog-kind"
            :data-testid="`tog-kind-${idx}`"
            @update:model-value="(v) => setKind(idx, v)"
          />

          <template v-if="!readOnly">
            <v-btn
              size="x-small"
              density="comfortable"
              variant="text"
              icon
              aria-label="เลื่อนกฎขึ้น"
              :disabled="idx === 0"
              :data-testid="`tog-up-${idx}`"
              @click="move(idx, -1)"
            >
              <v-icon size="18">mdi-arrow-up</v-icon>
              <v-tooltip activator="parent" location="top">เลื่อนขึ้น (รันก่อน)</v-tooltip>
            </v-btn>
            <v-btn
              size="x-small"
              density="comfortable"
              variant="text"
              icon
              aria-label="เลื่อนกฎลง"
              :disabled="idx === shownRows.length - 1"
              :data-testid="`tog-down-${idx}`"
              @click="move(idx, 1)"
            >
              <v-icon size="18">mdi-arrow-down</v-icon>
              <v-tooltip activator="parent" location="top">เลื่อนลง (รันทีหลัง)</v-tooltip>
            </v-btn>
            <v-btn
              size="x-small"
              density="comfortable"
              variant="text"
              icon
              color="error"
              aria-label="ลบกฎข้อนี้"
              :data-testid="`tog-remove-${idx}`"
              @click="remove(idx)"
            >
              <v-icon size="18">mdi-close</v-icon>
              <v-tooltip activator="parent" location="top">ลบกฎ</v-tooltip>
            </v-btn>
          </template>
        </div>

        <!-- ── กฎ regex ──────────────────────────────────────────────────────────────
             🔴 ช่อง "ค้นหา" เก็บ regex ดิบ: ผู้ใช้พิมพ์ `<\s*/?\s*brk\s*/?\s*>` ไม่ต้องพิมพ์ backslash คู่
             `JSON.stringify` ใน serializeRules เป็นคนเติม escape ให้ตอนบันทึก — นี่คือเหตุผลทั้งหมด
             ที่ตารางนี้แทน textarea JSON -->
        <div v-if="row.kind !== 'op'" class="tog-body">
          <div class="tog-find">
            <v-text-field
              :model-value="row.find"
              :readonly="readOnly"
              label="ค้นหา (regex)"
              placeholder="<\s*/?\s*brk\s*/?\s*>"
              variant="outlined"
              density="compact"
              hide-details="auto"
              class="tog-mono"
              :error-messages="errorsFor(idx, 'find')"
              :data-testid="`tog-find-${idx}`"
              @update:model-value="(v) => setField(idx, 'find', v)"
            />
            <v-checkbox-btn
              :model-value="row.ignoreCase === true"
              :readonly="readOnly"
              density="compact"
              hide-details
              color="primary"
              label="ไม่สนพิมพ์ใหญ่-เล็ก"
              class="tog-ic"
              :data-testid="`tog-ignorecase-${idx}`"
              @update:model-value="(v) => setField(idx, 'ignoreCase', v === true)"
            />
          </div>

          <!-- คำเตือนเท่านั้น ห้ามบล็อกการบันทึก — เอนจินจริงเป็น .NET และ backend ตรวจซ้ำตอนเซฟอยู่แล้ว -->
          <p v-if="regexWarnOf(row)" class="tog-warn" :data-testid="`tog-regex-warning-${idx}`">
            <v-icon size="14" color="warning">mdi-alert-outline</v-icon>
            {{ regexWarnOf(row) }}
          </p>

          <v-text-field
            :model-value="row.replace"
            :readonly="readOnly"
            label="แทนที่ด้วย"
            placeholder="<brk/>"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="tog-mono"
            :error-messages="errorsFor(idx, 'replace')"
            :data-testid="`tog-replace-${idx}`"
            @update:model-value="(v) => setField(idx, 'replace', v)"
          />
        </div>

        <!-- ── กฎสำเร็จรูป ──────────────────────────────────────────────────────────── -->
        <div v-else class="tog-body">
          <v-select
            :model-value="row.op"
            :items="KNOWN_OPS"
            item-title="labelTh"
            item-value="value"
            :readonly="readOnly"
            label="ทำอะไร"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :error-messages="errorsFor(idx, 'op')"
            :data-testid="`tog-op-${idx}`"
            @update:model-value="(v) => setField(idx, 'op', v)"
          />
          <p v-if="opHintOf(row)" class="tog-hint">{{ opHintOf(row) }}</p>
        </div>

        <v-text-field
          :model-value="row.note"
          :readonly="readOnly"
          label="บันทึกช่วยจำ (ระบบไม่ใช้)"
          placeholder="เช่น < brk/> · </brk> · <BRK/> → <brk/>"
          variant="outlined"
          density="compact"
          hide-details
          class="tog-note"
          :data-testid="`tog-note-${idx}`"
          @update:model-value="(v) => setField(idx, 'note', v)"
        />
      </div>

      <!-- `[]` — ต้องเห็นชัดว่าต่างจาก "ยังไม่ override" ไม่ใช่จอว่างเปล่าที่ตีความเองไม่ได้ -->
      <p v-if="!shownRows.length" class="tog-empty" data-testid="tog-empty">
        ไม่มีกฎในชุดนี้ — ตัวตรวจยังเดินผ่านทุกข้อความ แต่ไม่มีอะไรให้แก้
        (ถ้าตั้งใจจะกลับไปใช้ชุดเริ่มต้นในโค้ด ให้กด «กลับไปใช้ชุดเริ่มต้น» ด้านบน)
      </p>
    </div>

    <!-- ══════════════ โหมด JSON ขั้นสูง ══════════════ -->
    <v-textarea
      v-else
      :model-value="jsonText"
      label="กติกาทั้งชุด (JSON)"
      variant="outlined"
      density="compact"
      rows="10"
      auto-grow
      hide-details="auto"
      class="tog-mono"
      :placeholder="defaultJson"
      data-testid="tog-json"
      @update:model-value="setJson"
    >
      <template #append-inner>
        <!-- 🔴 `@mousedown.prevent`: ปุ่มอยู่ "ใน" ช่อง ถ้าไม่กันไว้ การกดจะโฟกัส textarea แล้ววาง caret
             ตรงที่เมาส์ลง — ในเอกสารที่กำลังจะถูกจัดรูปแบบใหม่ มันไม่เคยอยู่ตรงที่คนเขียนมองอยู่ -->
        <v-btn
          icon
          size="x-small"
          variant="text"
          :disabled="!canFormat"
          aria-label="จัดรูปแบบ JSON"
          data-testid="tog-json-beautify"
          @mousedown.prevent
          @click.stop="beautify"
        >
          <v-icon size="18">mdi-code-braces</v-icon>
          <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
        </v-btn>
      </template>
    </v-textarea>

    <!-- ══════════════ ท้ายตาราง ══════════════ -->
    <div class="tog-foot">
      <v-btn
        v-if="!showJson && !readOnly"
        size="small"
        variant="tonal"
        color="primary"
        prepend-icon="mdi-plus"
        :disabled="rows.length >= MAX_RULES"
        data-testid="tog-add"
        @click="addRule"
      >
        เพิ่มกฎ
      </v-btn>
      <span v-if="!showJson && !readOnly && rows.length >= MAX_RULES" class="tog-hint">
        ครบเพดาน {{ MAX_RULES }} ข้อแล้ว
      </span>

      <v-spacer />

      <v-btn
        size="small"
        variant="text"
        :disabled="showJson && !canLeaveJson"
        :prepend-icon="showJson ? 'mdi-table' : 'mdi-code-json'"
        data-testid="tog-toggle-json"
        @click="toggleJsonMode"
      >
        {{ showJson ? 'กลับไปโหมดตาราง' : 'แก้เป็น JSON (ขั้นสูง)' }}
        <v-tooltip v-if="showJson && !canLeaveJson" activator="parent" location="top">
          JSON ยังอ่านไม่ออก — แก้ให้ถูกก่อนจึงจะกลับไปโหมดตารางได้
        </v-tooltip>
      </v-btn>
    </div>

    <p class="tog-hint tog-foot-hint">
      ลำดับบนลงล่าง = ลำดับรัน กฎถัดไปทำงานบนผลของกฎก่อนหน้า ·
      กฎสำเร็จรูปจับ <code>&lt;brk/&gt;</code> แบบตรงตัว จึงควรอยู่ "หลัง" กฎ regex ที่ดัดรูปเพี้ยน ๆ ให้เป็นตัวมาตรฐาน
    </p>
  </div>
</template>

<script>
/**
 * TaxOutputGuardRulesEditor — the rule table behind แท็บ "ตรวจก่อนส่ง (Guard)" of
 * `TaxPlanningSettingsV2Dialog`. It edits ONE settings column (`outputGuardRulesJson`) and, from the dialog's
 * point of view, is just another control that reads a string and emits a string: `values[key]` stays a string
 * everywhere, so `displayValue` / `onEdit` / `isAtDefault` / `payloadValue` / `fieldErrors` / `applyDto` all
 * keep working untouched.
 *
 * <b>Why it replaced a textarea.</b> The column holds an array of repairs the node runs over every outgoing
 * message, and the interesting field of each one is a regular expression. In a JSON textarea an author has to
 * write that expression twice-escaped (`<\\s*…`), get commas and braces right, and read run order out of array
 * position — three separate ways to produce a rule that silently never fires. Here the pattern box holds the
 * RAW regex, `serializeRules` does the JSON escaping, and run order is a numbered list with arrows.
 *
 * 🔴 <b>It must not write to the column just because it read it.</b> The dialog's "ยังไม่ได้บันทึก" flag is a raw
 * string compare against a snapshot taken at load. So this component NEVER emits on mount or on hydrate — it
 * parses, shows, and waits — and even a real edit goes through `rulesEqual` first, so a change that means the
 * same thing (whitespace, key order) does not raise that flag either.
 *
 * 🔴 <b>Blank, `[]` and a list are three answers, not two.</b> Blank = ยังไม่ override (the engine runs the
 * built-in set); `[]` = override ที่ว่าง (the guard still runs, with nothing to run); a list = run exactly this.
 * «กลับไปใช้ชุดเริ่มต้น» therefore emits `''` and NEVER the default JSON — writing the default text back would
 * store an override identical to the default, and the Playground's provenance readout would flip from
 * "default ในโค้ด" to "config:" for a version nobody had actually customised.
 */
import { canFormatJsonText, formatJsonText } from '../utils/jsonText.js';
import {
  KNOWN_OPS,
  MAX_RULES,
  blankRule,
  parseRules,
  regexWarning,
  rulesEqual,
  serializeRules,
  validateRules,
} from '../utils/taxOutputGuardRules.js';

/** The two shapes a rule can take. `op` is the builtin list; `regex` is find/replace. */
const KIND_ITEMS = [
  { title: 'ค้นแล้วแทนที่ (regex)', value: 'regex' },
  { title: 'กฎสำเร็จรูป', value: 'op' },
];

export default {
  name: 'TaxOutputGuardRulesEditor',
  props: {
    /** the column's current value — a JSON array as text, or '' for "ยังไม่ override" */
    modelValue: { type: String, default: '' },
    /** the built-in set (`defaults.outputGuardRulesJson`), shown read-only until the author adopts it */
    defaultJson: { type: String, default: '' },
    /** the dialog's own `fieldErrors` for this key — kept, because JSON mode can still break the value */
    errorMessages: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      KNOWN_OPS,
      KIND_ITEMS,
      MAX_RULES,
      /** the editable rows — hydrated from `modelValue`, never written back unless the author edits */
      rows: [],
      /** advanced escape hatch: edit the array as raw text */
      jsonMode: false,
      jsonText: '',
      /**
       * 🔴 The exact string this component last emitted. The parent echoes it straight back through
       * `modelValue`, and re-hydrating on that echo would rebuild every row object mid-keystroke (and hand the
       * author back a TRIMMED id while they are still typing it). So the watcher ignores its own echo and
       * re-hydrates only when the value changed somewhere else — loading another version, an import, a revert.
       */
      lastEmitted: null,
    };
  },
  computed: {
    parsed() {
      return parseRules(this.modelValue);
    },
    /** 'default' (blank) · 'custom' (a list) · 'empty' (`[]`) · 'broken' (does not parse) */
    mode() {
      if (this.parsed.blank) return 'default';
      if (this.parsed.error) return 'broken';
      return this.parsed.rows.length ? 'custom' : 'empty';
    },
    /** With no override the table SHOWS the built-in set (so the rules in force are readable) but nothing on it
     *  can be changed — the author adopts a copy first, which is what makes the override explicit. */
    readOnly() {
      return this.mode === 'default';
    },
    defaultRows() {
      return parseRules(this.defaultJson).rows;
    },
    shownRows() {
      return this.readOnly ? this.defaultRows : this.rows;
    },
    /** A document that does not parse has no table to draw, so the raw editor is the only way back. */
    showJson() {
      return this.jsonMode || this.mode === 'broken';
    },
    canLeaveJson() {
      return this.mode !== 'broken';
    },
    canFormat() {
      return canFormatJsonText(this.jsonText);
    },
    validation() {
      return validateRules(this.shownRows);
    },
    setErrors() {
      return this.validation.filter((e) => e.index < 0).map((e) => e.messageTh);
    },
    statusProps() {
      if (this.mode === 'broken') return { type: 'error', icon: 'mdi-alert-circle-outline' };
      if (this.mode === 'empty') return { type: 'warning', icon: 'mdi-cancel' };
      if (this.mode === 'default') return { type: 'info', icon: 'mdi-information-outline' };
      return { color: 'primary', icon: 'mdi-playlist-edit' };
    },
    statusText() {
      if (this.mode === 'broken') {
        return `อ่านกติกาไม่ออก — ${this.parsed.error} · แก้ในโหมด JSON ด้านล่าง หรือกลับไปใช้ชุดเริ่มต้น`;
      }
      if (this.mode === 'empty') {
        return 'ปิดทุกกฎ (ตัวตรวจยังทำงานแต่ไม่มีกฎให้รัน) — ต่างจาก "ใช้ชุดเริ่มต้น" ตรงที่ชุดในโค้ดถูกทับทิ้งด้วย';
      }
      if (this.mode === 'default') {
        return `กำลังใช้ชุดเริ่มต้นในโค้ด (${this.defaultRows.length} ข้อ) — ยังไม่ได้ตั้งกติกาของเวอร์ชันนี้ ข้างล่างคือชุดที่กำลังทำงานอยู่จริง`;
      }
      return `กติกาของเวอร์ชันนี้ ${this.rows.length} ข้อ — ทับชุดเริ่มต้นในโค้ดทั้งชุด`;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        // 🔴 Our own echo — ignore it (see `lastEmitted`). Everything else is a real load.
        if (value === this.lastEmitted) return;
        this.hydrate(value);
      },
    },
  },
  methods: {
    /** Read the column into rows. 🔴 NEVER emits — this is the "ห้าม re-serialize ตอนโหลด" rule. */
    hydrate(value) {
      const text = value === null || value === undefined ? '' : String(value);
      this.rows = parseRules(text).rows;
      this.jsonText = text;
      this.jsonMode = false;
    },
    /** Write the rows back, but only when they actually SAY something different. */
    commit() {
      const text = serializeRules(this.rows);
      if (rulesEqual(text, this.modelValue)) return;
      this.emitValue(text);
    },
    emitValue(text) {
      this.lastEmitted = text;
      this.jsonText = text;
      this.$emit('update:modelValue', text);
    },
    errorsFor(index, field) {
      return this.validation.filter((e) => e.index === index && e.field === field).map((e) => e.messageTh);
    },
    /** Warning only — the server compiles with .NET and refuses there if it must. */
    regexWarnOf(row) {
      if (!row || row.kind === 'op') return null;
      return regexWarning(row.find);
    },
    opHintOf(row) {
      const found = KNOWN_OPS.find((k) => k.value === (row && row.op));
      return found ? found.hintTh : '';
    },
    setField(index, key, value) {
      const row = this.rows[index];
      if (!row) return;
      row[key] = key === 'enabled' || key === 'ignoreCase' ? value === true : String(value === null || value === undefined ? '' : value);
      this.commit();
    },
    /**
     * Switch a rule between regex and builtin. The row keeps BOTH sets of fields in memory so a mis-click does
     * not throw away a pattern someone just typed; `serializeRules` writes only the ones that belong to the
     * kind, so the stored document is never ambiguous.
     */
    setKind(index, value) {
      const row = this.rows[index];
      if (!row) return;
      row.kind = value === 'op' ? 'op' : 'regex';
      this.commit();
    },
    addRule() {
      if (this.rows.length >= MAX_RULES) return;
      this.rows.push(blankRule());
      this.commit();
    },
    /** Order IS behaviour — rule N runs on the output of rule N-1 — so this is a real edit, not a view sort. */
    move(index, delta) {
      const target = index + delta;
      if (index < 0 || index >= this.rows.length || target < 0 || target >= this.rows.length) return;
      const [row] = this.rows.splice(index, 1);
      this.rows.splice(target, 0, row);
      this.commit();
    },
    /** 🔴 Removing the last rule leaves `[]`, never '' — "ปิดทุกกฎ" is not "กลับไปใช้ชุดเริ่มต้น". */
    remove(index) {
      if (index < 0 || index >= this.rows.length) return;
      this.rows.splice(index, 1);
      this.commit();
    },
    /** Copy the built-in set into editable rows. This is the moment an override starts existing. */
    adoptDefaults() {
      const parsedDefaults = parseRules(this.defaultJson);
      this.rows = parsedDefaults.rows.length ? parsedDefaults.rows : [blankRule()];
      this.emitValue(serializeRules(this.rows));
    },
    /** 🔴 '' — the ONLY way back to "no override". Writing the default JSON here would store an override. */
    revertToDefaults() {
      this.rows = [];
      this.jsonMode = false;
      this.emitValue('');
    },
    /**
     * Raw JSON edits go up VERBATIM, including text that does not parse: the dialog's `fieldErrors` is what
     * shows the author the problem and blocks the save, and it can only do that if it receives the broken text.
     */
    setJson(value) {
      const text = value === null || value === undefined ? '' : String(value);
      this.jsonText = text;
      this.lastEmitted = text;
      const parsedNow = parseRules(text);
      if (!parsedNow.error) this.rows = parsedNow.rows;
      this.$emit('update:modelValue', text);
    },
    beautify() {
      const formatted = formatJsonText(this.jsonText);
      if (formatted === null) return;   // ไม่ใช่ JSON ⇒ ไม่แตะ (jsonText.js RULE 2)
      this.setJson(formatted);
    },
    toggleJsonMode() {
      if (this.showJson) {
        if (!this.canLeaveJson) return;
        this.jsonMode = false;
        this.rows = this.parsed.rows;
        return;
      }
      // เข้าโหมด JSON: แสดงค่าที่เก็บอยู่จริง (ว่างก็คือว่าง — placeholder โชว์ชุดเริ่มต้นให้ดูเป็นตัวอย่าง)
      this.jsonText = this.modelValue === null || this.modelValue === undefined ? '' : String(this.modelValue);
      this.jsonMode = true;
    },
  },
};
</script>

<style scoped>
/**
 * 🔴 The parent's `<style scoped>` does not reach in here, so every rule this component needs lives in this
 * block. The `--tps-*` ramp DOES reach it (custom properties inherit through the DOM, and this renders inside
 * `.tpsv2`), so the greyscale stays the dialog's own — no literal colours, and nothing off the theme.
 */
.tog {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tog-status :deep(.v-alert__content) { width: 100%; }
.tog-status,
.tog-alert { font-size: 0.82rem; line-height: 1.55; }

.tog-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tog-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--tps-line, rgba(128, 128, 128, 0.2));
  border-radius: 10px;
  background: var(--tps-panel, transparent);
}
/* ปิดกฎไว้ = ยังอยู่ในลำดับ แต่ไม่ทำงาน — จางลงพอให้เห็นต่าง ไม่ถึงกับอ่านไม่ออก */
.tog-row.is-off { opacity: 0.62; }

.tog-row-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.tog-index {
  min-width: 1.4em;
  font-size: 0.78rem;
  font-weight: 500;
  text-align: right;
  color: var(--tps-ink-3, inherit);
}
.tog-id { flex: 1 1 190px; min-width: 150px; }
.tog-kind { flex: 0 1 210px; min-width: 170px; }

.tog-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 2px;
}
.tog-find {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}
.tog-find .tog-mono { flex: 1 1 260px; min-width: 200px; }
.tog-ic { flex: 0 0 auto; padding-top: 6px; }
.tog-ic :deep(.v-label) { font-size: 0.8rem; opacity: 1; }

/* regex อ่านทีละตัวอักษร — สัดส่วนคงที่ทำให้นับ \s กับ ? ได้จริง */
.tog-mono :deep(input),
.tog-mono :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.83rem;
}

.tog-hint,
.tog-warn,
.tog-empty {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.6;
  color: var(--tps-ink-3, inherit);
}
.tog-warn { color: rgb(var(--v-theme-warning)); }
.tog-empty {
  padding: 14px 12px;
  border: 1px dashed var(--tps-line, rgba(128, 128, 128, 0.2));
  border-radius: 10px;
  text-align: center;
}

.tog-foot {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tog-foot-hint { max-width: 76ch; }
.tog-foot-hint code { font-size: 0.76rem; }
</style>
